import { connectedMessageId } from "../../supabase/functions/_shared/connectedMessageDomain";
import { validateConnectedMessageJournal, validateConnectedMessageReply } from "@/lib/connectedMessageWriteProof";
import type { PendingConnectorWrite } from "@/lib/connectorPendingWrite";

export interface MirrorConnectedMessageReceiptReference {
  write_contract: "mirror_connected_message_write_v1";
  provider: "slack" | "microsoft_teams";
  action: "send";
  capability: "expansion:send";
  idempotency_key: string;
  action_id: string;
  userId: string;
  sessionId: string;
  request_hash: string;
  receipt_id: string;
  authSessionId: string;
  connection_id: string;
  external_id: string;
  readback_hash: string;
}
export interface MirrorConnectedMessageOrigin { userId: string; sessionId: string; actionId: string }
const uuid = (value: unknown): value is string => typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(value);
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
export function matchesMirrorConnectedMessageReceiptOrigin(value: unknown, origin: MirrorConnectedMessageOrigin): value is MirrorConnectedMessageReceiptReference {
  if (!object(value) || value.write_contract !== "mirror_connected_message_write_v1" ||
    !uuid(origin.userId) || !uuid(origin.sessionId) || !uuid(origin.actionId) || origin.actionId === origin.sessionId ||
    value.userId !== origin.userId || value.sessionId !== origin.sessionId || value.action_id !== origin.actionId ||
    !uuid(value.authSessionId) || !uuid(value.connection_id) || !uuid(value.receipt_id) || typeof value.readback_hash !== "string" || !/^[0-9a-f]{64}$/u.test(value.readback_hash) || typeof value.request_hash !== "string" || !/^[0-9a-f]{64}$/u.test(value.request_hash) ||
    !["slack", "microsoft_teams"].includes(String(value.provider)) ||
    !["send"].includes(String(value.action)) ||
    value.idempotency_key !== "mirror_connected_message_" + origin.actionId + "_" + value.action) return false;
  try { connectedMessageId(value.provider as "slack" | "microsoft_teams", value.external_id); } catch { return false; }
  return value.capability === "expansion:send";
}
/** Only a freshly verified response supplies a locator; journal state alone cannot. */
export function mirrorConnectedMessageReceiptReference(entry: PendingConnectorWrite, origin: MirrorConnectedMessageOrigin): MirrorConnectedMessageReceiptReference | null {
  const proof = entry.confirmedResult;
  if (!validateConnectedMessageJournal(entry, origin.userId) || !validateConnectedMessageReply(proof, entry.body, (proof as { phase?: string })?.phase === "execute" ? "execute" : "receipt_status")) return null;
  if (entry.group !== "connected_message" || entry.ownerId !== origin.userId || entry.key !== origin.userId + ":connected_message" ||
    entry.status !== "succeeded" || !object(proof) || !object(proof.result) || proof.status !== "succeeded") return null;
  const body = entry.body;
  if (!object(body.mirror_context) || body.mirror_context.session_id !== origin.sessionId || body.mirror_context.action_id !== origin.actionId ||
    proof.owner_id !== origin.userId || proof.auth_session_id !== body.auth_session_id ||
    proof.request_hash !== body.request_hash || proof.provider !== body.provider || proof.action !== body.action ||
    proof.idempotency_key !== body.idempotency_key) return null;
  const reference: unknown = {
    write_contract: "mirror_connected_message_write_v1", provider: body.provider, action: body.action,
    capability: "expansion:send",
    idempotency_key: body.idempotency_key, action_id: origin.actionId, userId: origin.userId, sessionId: origin.sessionId,
    request_hash: proof.request_hash, receipt_id: proof.receipt_id, authSessionId: proof.auth_session_id,
    connection_id: proof.result.connection_id, external_id: proof.result.external_id, readback_hash: proof.result.readback_hash,
  };
  return matchesMirrorConnectedMessageReceiptOrigin(reference, origin) ? Object.freeze(reference) : null;
}
